import { LabelImportantTwoTone } from "@mui/icons-material";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import * as Yup from "yup";
import { useLazyGetByRoleQuery } from "../../../redux/api/applicationCopyrightApiSlice";
import { useGetRoleNotInQuery } from "../../../redux/api/categoryApiSlice";
import { useReconsiderationFileSubmitMutation } from "../../../redux/api/submittedCopyrightAgreementApiSlice";
import { useAuth } from "../../../utils/authorization";
import { localDateFormat, localTimeFormat } from "../../../utils/dateFormat";
import { transformErrorsToObjectStructure } from "../../../utils/transformErrorsToObjectStructure";

const ReconsiderationCommon = ({ comments, info, breadcrumbsTitle }) => {
  const { authUser } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [
    fileSubmit,
    {
      isLoading: isLoadingSubmit,
      isSuccess: isSuccessSubmit,
      error: errorSubmit,
      isError: isErrorSubmit,
      data: dataSubmit,
    },
  ] = useReconsiderationFileSubmitMutation();

  const rolesIds = info.fileStatus === 33 ? "[1,9,10]" : "[1,9,10,12]";

  const { data: roles } = useGetRoleNotInQuery(rolesIds);
  const rolesOption =
    roles?.data?.map((data) => ({
      value: data.id,
      label: data.bn_name,
    })) || [];

  const [findUser, { data: users, isLoading }] = useLazyGetByRoleQuery();

  const usersOption =
    (users?.data &&
      Object.entries(users.data)
        .filter(([key]) => authUser.id !== Number(key))
        .map(([key, value]) => ({
          value: Number(key),
          label: value,
        }))) ||
    [];

  const validationSchema = Yup.object().shape({
    comments: Yup.string().when("submit", {
      is: (val) => val !== 2,
      then: () => Yup.string().required("মন্তব্য প্রযোজ্য।"),
      otherwise: () => Yup.string().nullable(),
    }),
    role_id: Yup.number().when("submit", {
      is: (val) => val !== 2,
      then: () => Yup.number().required("পদবী প্রযোজ্য।"),
      otherwise: () => Yup.number().nullable(),
    }),
    user_id: Yup.number().when("submit", {
      is: (val) => val !== 2,
      then: () => Yup.number().required("প্রাপক প্রযোজ্য।"),
      otherwise: () => Yup.number().nullable(),
    }),
  });

  const formik = useFormik({
    initialValues: {
      comments: "",
      role_id: "",
      user_id: "",
      submit: "",
    },
    validationSchema,
    onSubmit: (values) => {
      const formData = values;
      Swal.fire({
        text: "আপনি কি নিশ্চিত?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "না",
        confirmButtonText: "হ্যাঁ",
      }).then((result) => {
        if (result.isConfirmed) {
          fileSubmit({ id, formData, fileStatus: info.fileStatus });
        }
      });
    },
  });
  useEffect(() => {
    if (isSuccessSubmit) {
      toast.success(dataSubmit?.message);
      formik.resetForm();
      navigate(-1);
    }
    if (isErrorSubmit) {
      toast.error(errorSubmit?.data?.message);
      formik.setErrors(
        transformErrorsToObjectStructure(errorSubmit.data?.data || {})
      );
      if (errorSubmit.data.data) {
        formik.setErrors({
          comments: errorSubmit.data.data.comments?.[0],
          role_id: errorSubmit.data.data.role_id?.[0],
          user_id: errorSubmit.data.data.user_id?.[0],
        });
      }
    }
    // eslint-disable-next-line
  }, [isErrorSubmit, isSuccessSubmit]);

  const defaultValue = {
    value: "",
    label: "নির্বাচন করুন",
  };

  return (
    <>
      <div
        className="bg-slate px-2 fw-semibold"
        style={{
          padding: "10px",
        }}
      >
        {breadcrumbsTitle}
      </div>
      <div className="pt-3">
        <div className="py-2">
          <span className="fw-semibold">
            <LabelImportantTwoTone fontSize="17px" /> হইতে,
          </span>{" "}
          {info?.fromAdmin?.name} ({info?.fromAdmin?.role?.bnName})
        </div>
      </div>

      <form onSubmit={formik.handleSubmit}>
        <label className="my-2 fw-semibold" htmlFor="comments">
          মন্তব্য
        </label>
        <Form.Control
          name="comments"
          placeholder="এখানে লিখুন"
          aria-label="Username"
          aria-describedby="basic-addon1"
          as={"textarea"}
          rows={6}
          value={formik.values.comments}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          isInvalid={formik.touched.comments && formik.errors.comments}
        />
        {formik.touched.comments && formik.errors.comments ? (
          <div className="text-danger">{formik.errors.comments}</div>
        ) : null}
        <div className="text-end py-3">
          <div>
            <div className="d-flex justify-content-end">
              <div className="text-center">
                <div className="text-primary">{info?.admin?.name}</div>
                <div className="text-primary">{info?.admin?.role?.bnName}</div>
                <div>তারিখ : {localDateFormat(new Date())}</div>
                <div>সময় : {localTimeFormat(new Date())}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <label className="my-2 fw-semibold" htmlFor="role_id">
              পদবী
            </label>
            <Select
              name="role_id"
              options={rolesOption}
              value={
                formik.values.role_id
                  ? rolesOption.find(
                      (option) => option.value === formik.values.role_id
                    )
                  : defaultValue
              }
              onChange={(selectedOption) => {
                findUser(selectedOption.value);
                formik.setValues((prev) => {
                  return {
                    ...prev,
                    user_id: "",
                  };
                });
                formik.setFieldValue("role_id", selectedOption.value);
              }}
              onBlur={formik.handleBlur}
              classNamePrefix="react-select"
            />
            {formik.touched.role_id && formik.errors.role_id ? (
              <div className="text-danger">{formik.errors.role_id}</div>
            ) : null}
          </div>
          <div className="col-md-6">
            <label className="my-2 fw-semibold" htmlFor="user_id">
              প্রাপক
            </label>
            <Select
              name="user_id"
              options={usersOption}
              isDisabled={isLoading}
              isLoading={isLoading}
              value={
                formik.values.user_id
                  ? usersOption.find(
                      (option) => option.value === formik.values.user_id
                    )
                  : defaultValue
              }
              onChange={(selectedOption) =>
                formik.setFieldValue("user_id", selectedOption.value)
              }
              onBlur={formik.handleBlur}
              classNamePrefix="react-select"
            />
            {formik.touched.user_id && formik.errors.user_id ? (
              <div className="text-danger">{formik.errors.user_id}</div>
            ) : null}
          </div>
        </div>
        <div className="row mt-4">
          {authUser.role_id !== 8 && (
            <div className="col-md-4 mt-2">
              <Button
                className="w-100"
                variant="secondary text-white"
                onClick={() => formik.setFieldValue("submit", 17)}
                type="submit"
                disabled={isLoadingSubmit}
              >
                অতিরিক্ত ব্যাখ্যা
              </Button>
            </div>
          )}
          <div className="col-md-4 mt-2">
            <Button
              className="w-100"
              variant="primary text-white"
              onClick={() => formik.setFieldValue("submit", 18)}
              type="submit"
              disabled={isLoadingSubmit}
            >
              ব্যাখ্যার জবাব
            </Button>
          </div>
          {info.fileStatus === 33 && (
            <div className="col-md-4 mt-2">
              <Button
                className="w-100"
                variant="success"
                onClick={() => formik.setFieldValue("submit", 5)}
                type="submit"
                disabled={isLoadingSubmit || formik.values.role_id === 2}
              >
                {formik.values.role_id === 12 || !formik.values.role_id
                  ? "চূড়ান্তভাবে অনুমোদিত"
                  : breadcrumbsTitle}
              </Button>
            </div>
          )}
          {info.fileStatus === 34 && (
            <div className="col-md-4 mt-2">
              <Button
                className="w-100"
                variant="success"
                onClick={() => formik.setFieldValue("submit", 19)}
                type="submit"
                disabled={isLoadingSubmit || formik.values.role_id === 2}
              >
                {formik.values.role_id === 7 || !formik.values.role_id
                  ? "চূড়ান্তভাবে নথিজাত"
                  : breadcrumbsTitle}
              </Button>
            </div>
          )}
          {info.fileStatus === 35 && (
            <div className="col-md-4 mt-2">
              <Button
                className="w-100"
                variant="success"
                onClick={() => formik.setFieldValue("submit", 6)}
                type="submit"
                disabled={isLoadingSubmit || formik.values.role_id === 2}
              >
                {formik.values.role_id === 7 || !formik.values.role_id
                  ? "চূড়ান্তভাবে অননুমোদন"
                  : breadcrumbsTitle}
              </Button>
            </div>
          )}
        </div>
      </form>
    </>
  );
};
export default ReconsiderationCommon;
